import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Textarea from 'react-autosize-textarea';
import moment from 'moment';

import { markdownWithoutHTML } from '../markdown';
import cable from '../cable';
import Spinner from './Spinner';

export default class MessagesConnectionDetails extends Component {
  static propTypes = {
    messagesApi: PropTypes.string.isRequired,
    connectionsApi: PropTypes.string.isRequired,
    selectedConnectionId: PropTypes.string,
    startConnection: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    onNewConnection: PropTypes.func.isRequired,
    markAsRead: PropTypes.func.isRequired,
    analytics: PropTypes.string,
  };

  static defaultProps = {
    selectedConnectionId: null,
    startConnection: null,
    analytics: null,
  };

  constructor(props) {
    super(props);
    this.textarea = React.createRef();
    this.state = {
      messages: [],
      loading: false,
    };
  }



  startConnection(data, afterStartConnectionCallback) {
    const connection = { recipient_id: data.recipient.id };

    $.post(this.props.connectionsApi, { connection }, response => {
      Union.log('Connection started successfully', response);

      this.subscribe(response.id);
      afterStartConnectionCallback(response.id, () => {
        this.props.onNewConnection(response.id);
      });
    });
  }

  submitMessage(connectionId, body, afterSubmitMessageCallback = () => {}) {
    $.post(
      this.props.messagesApi.replace(':connection_id', connectionId),
      { message: { body } },
      () => {
        Union.log('Message created successfully');
        afterSubmitMessageCallback();
      }
    );
  }

  handleSubmit = e => {
    if (e) e.preventDefault();

    const body = this.textarea.current.value;
    if (!body) return;

    const connectionId = this.props.selectedConnectionId;

    this.textarea.current.value = '';
    this.textarea.current.focus();

    if (!connectionId) {
      this.startConnection(this.props.startConnection, (newConnectionId, callback) => {
        this.submitMessage(newConnectionId, body, callback);
      });
    } else {
      this.submitMessage(connectionId, body);
    }
  };

  handleFocus = () => {
    this.props.markAsRead(this.props.selectedConnectionId);
  };

  fetchMessages(connectionId) {
    if (!connectionId) {
      this.setState({ loading: false, messages: [] });
      return;
    }

    this.setState({ loading: true, messages: [] });

    $.get(this.props.messagesApi.replace(':connection_id', connectionId), messages => {
      this.setState({
        loading: false,
        messages,
      });
    });
  }

  subscribe(connectionId) {
    Union.log('Subscribing to connection', connectionId);
    if (!connectionId) return;

    this.subscription = cable.subscriptions.create(
      {
        channel: 'MessagesChannel',
        connection_id: connectionId,
      },
      {
        received: data => {
          const { messages } = this.state;
          this.setState({ messages: messages.concat(data) });
        },
      }
    );
  }

  unsubscribe(connectionId) {
    if (this.subscription) {
      Union.log('Unsubscribing from connection', connectionId);
      this.subscription.unsubscribe();
    }
  }

  scrollToBottom = () => {
    const scrollHeight = this.list.scrollHeight;
    const height = this.list.clientHeight;
    const maxScrollTop = scrollHeight - height;

    this.list.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
  };

  componentWillUpdate(nextProps, nextState) {
    if (nextProps.selectedConnectionId !== this.props.selectedConnectionId) {
      this.unsubscribe(this.props.selectedConnectionId);
      this.fetchMessages(nextProps.selectedConnectionId);
      this.subscribe(nextProps.selectedConnectionId);
      this.textarea.current.value = '';
    }

    if (nextState.messages.length !== this.state.messages.length) {
      const scrollPos = this.list.scrollTop;
      const scrollBottom = this.list.scrollHeight - this.list.clientHeight;
      this.scrollAtBottom = scrollBottom <= 0 || scrollPos === scrollBottom;
    }
  }

  componentDidUpdate() {
    if (this.scrollAtBottom) {
      this.scrollToBottom();
    }
  }

  componentDidMount() {
    const connectionId = this.props.selectedConnectionId;
    this.fetchMessages(connectionId);
    this.subscribe(connectionId);
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  renderDateSeparator(timestamp) {
    return (
      <em className="u-block u-text-grey u-not-italic u-text-center u-my-4">
        {moment(timestamp).calendar(null, {
          sameDay: '[Today]',
          nextDay: '[Tomorrow]',
          lastDay: '[Yesterday]',
          lastWeek: 'MMMM D',
          sameElse: 'MMMM D',
        })}
      </em>
    );
  }

  renderTimestamp(timestamp) {
    return (
      <em className="u-px-3 u-text-grey u-not-italic u-flex-none u-select-none">
        {moment(timestamp).format('h:mm A')}
      </em>
    );
  }

  renderMessage(message) {
    const { currentUser } = this.props;
    const me = message.sender_id === currentUser.id;
    const currentDate = message.created_at;

    return (
      <div
        key={message.id}
        className={`u-my-3 u-flex u-items-center u-justify-between lg:u-justify-start u-text-sm u-font-sans ${
          me ? 'lg:u-justify-end' : ''
        }`}>
        <div className="u-hidden lg:u-block">
          {!me && this.renderTimestamp(currentDate)}
        </div>
        <div
          className={`Readability messages-text u-px-3 u-py-2 u-inline-block u-rounded-lg u-text-left u-max-w-2/3 md:u-max-w-sm ${
            me ? 'messages-text--me' : 'u-bg-grey-lightest'
            }`}
          dangerouslySetInnerHTML={{ __html: markdownWithoutHTML.render(message.body) }}
        />
        <div className="lg:u-hidden">
          {!me && this.renderTimestamp(currentDate)}
        </div>
        {me && this.renderTimestamp(currentDate)}
      </div>
    );
  }

  renderMessages() {
    let lastDate;

    return (
      <div
        className="u-flex-1 u-px-4 u-pt-2 u-overflow-auto u-cursor-default"
        ref={node => {
          this.list = node;
        }}>
        {this.state.messages.map(message => {
          const currentDate = message.created_at;
          if (!lastDate || moment(currentDate).isAfter(lastDate, 'day')) lastDate = currentDate;

          if (lastDate === currentDate) {
            return [this.renderDateSeparator(lastDate), this.renderMessage(message)];
          }
          return this.renderMessage(message);
        })}
      </div>
    );
  }

  renderForm() {
    return (
      <form
        onSubmit={this.handleSubmit}
        className="u-fixed u-bottom-0 u-inset-x-0 u-bg-white md:u-relative u-flex-none u-flex u-flex-row u-border-t u-border-grey-lighter u-items-end">
        <Textarea
          className="u-w-full u-p-6 u-pb-8 u-resize-none u-m-0 u-border-0 focus:u-border-grey-light u-h-24"
          placeholder="Type your message…"
          onFocus={this.handleFocus}
          ref={this.textarea}
        />
        <button className="u-font-sans u-font-normal u-border-blue u-border u-shadow u-bg-blue u-text-white u-rounded u-text-sm u-m-4 u-px-3 u-py-2 u-flex-1 hover:u-bg-blue-dark hover:u-text-white hover:u-border-blue-dark">
          Send
        </button>
      </form>
    );
  }

  render() {
    const { loading } = this.state;

    return (
      <div className="u-flex-1 u-flex u-flex-col md:u-pb-0" style={{ height: 'calc(100% - 5.5rem)' }}>
        {loading && (
          <div className="u-p-6 u-leading-none u-text-grey">
            <Spinner />
          </div>
        )}
        {this.renderMessages()}
        {this.renderForm()}
      </div>
    );
  }
}
